import { useState } from "react";
import { useForm, FieldValues } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import LinkService from "@/app/_services/LinkService";
import { linkSchema } from "./LinkSchema";
import { toast } from "@/app/_utils/toast";

type FormData = yup.InferType<typeof linkSchema>;

const EMPTY_LINK: FormData = {
  name: "",
  redirect: "",
  medium: "",
  source: "",
  preview_title: "",
  preview_description: "",
};

export function useLinkFormFlow({
  defaultLink,
  onSaved,
}: {
  defaultLink?: any;
  onSaved: (createdLink?: any) => void;
}) {
  const [isSaving, setIsSaving] = useState(false);
  // Data URL for a newly picked share image, or "" to clear the existing one.
  // null means untouched, so it is left out of the payload entirely.
  const [selectedImage, setSelectedImage] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<FormData>({
    // `values` (not `defaultValues`) so re-opening the dialog for a different
    // link re-syncs the fields, and only the form fields are mapped in.
    values: defaultLink
      ? {
          name: defaultLink.name ?? "",
          redirect: defaultLink.redirect ?? "",
          medium: defaultLink.medium ?? "",
          source: defaultLink.source ?? "",
          preview_title: defaultLink.preview_title ?? "",
          preview_description: defaultLink.preview_description ?? "",
        }
      : EMPTY_LINK,
    resolver: yupResolver(linkSchema),
  });

  const handleSave = handleSubmit(async (fields: FieldValues) => {
    setIsSaving(true);
    // `!== null` so the "" clear sentinel is still sent — the server treats an
    // empty image as "remove the preview image".
    const payload = selectedImage !== null ? { ...fields, image: selectedImage } : fields;
    try {
      if (defaultLink) {
        await LinkService.updateLinkAsync(defaultLink.id, payload as any);
        toast.success("Link updated");
        setSelectedImage(null);
        onSaved();
      } else {
        const created = await LinkService.createLinkAsync(payload as any);
        toast.success("Link created");
        reset(EMPTY_LINK);
        setSelectedImage(null);
        onSaved(created);
      }
    } catch (e) {
      toast.error("Failed to save link");
    } finally {
      setIsSaving(false);
    }
  });

  function discardChanges() {
    reset();
    setSelectedImage(null);
  }

  return {
    control,
    errors,
    isSaving,
    isDirty: isDirty || selectedImage !== null,
    handleSave,
    discardChanges,
    selectedImage,
    setSelectedImage,
  };
}
